import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { imagesBaseUrl } from "../../constants";
import type { Screenshot } from "../package";

@customElement("umb-market-carousel-card")
export class UmbMarketCarouselCard extends LitElement {
  #model?: Screenshot;

  @property({ type: Object })
  set model(value: Screenshot | undefined) {
    if (!value) return;

    this.#model = value;
    this.trySetVideoUrl(value.deliveryPath);
  }

  @property({ type: Boolean })
  fullscreen = false;

  #videoUrl?: string;

  /**
   * For now, we handle youtube embed/watch and vimeo
   * Any other value is rendered as a link as we don't know what it is
   */
  trySetVideoUrl(value: string | undefined) {
    if (!value) {
      this.#videoUrl = undefined;
      return;
    }

    if (
      value.includes("youtube.com/embed") ||
      value.includes("player.vimeo.com/video/")
    ) {
      this.#videoUrl = value;
      return;
    }

    if (value.includes("youtube.com/watch?")) {
      const videoId = value.split("?v=")[1];
      this.#videoUrl = `https://www.youtube.com/embed/${videoId}?autoplay=0&modestbranding=1`;
      return;
    }

    if (value.includes("youtu.be/")) {
      const videoId = value.split("/").pop();
      this.#videoUrl = `https://www.youtube.com/embed/${videoId}?autoplay=0&modestbranding=1`;
    }
  }

  #expand() {
    this.dispatchEvent(
      new CustomEvent("expand", {
        bubbles: true,
        composed: true,
      })
    );
  }

  #getImagePath() {
    return `${imagesBaseUrl}${this.#model!.deliveryPath}${
      this.fullscreen ? "" : "?width=750"
    }`;
  }

  render() {
    return html` <div
      tabindex="-1"
      class=${this.#videoUrl ? "video-slide" : "image-slide"}
    >
      ${when(
        !this.#videoUrl,
        () =>
          html`<umb-market-button tabindex="-1" @click=${this.#expand}>
            <span class="outline">Expand</span>
            <figure>
              <img src=${this.#getImagePath()} alt="" />
              ${when(
                this.fullscreen || this.#model?.caption,
                () =>
                  html`<figcaption>${this.#model?.caption ?? ""}</figcaption>`
              )}
            </figure>
          </umb-market-button> `,
        () =>
          html` <figure>
            <iframe src=${this.#videoUrl!} title=${this.#videoUrl!}></iframe>
            ${when(
              this.fullscreen || this.#model?.caption,
              () =>
                html`<figcaption>${this.#model?.caption ?? ""}</figcaption>`
            )}
          </figure>`
      )}
    </div>`;
  }

  static styles = [
    css`
      :host {
        min-width: 100%;
        display: block;
        box-sizing: border-box;
        border-radius: 22px;
        background: var(--dominant-color, var(--uui-color-current));
        padding: var(--s-spacer);
        align-self: flex-start;
      }

      :host(:focus) {
        outline: none;
        border: none;
      }

      :host([fullscreen]) {
        padding: var(--xs-spacer);
        border-radius: 12px;
      }

      @media (min-width: 767px) {
        :host {
          min-width: calc(var(--carousel-card-width, 50%) - 16px);
        }

        :host([fullscreen]) {
          max-height: 80vh;
          min-width: 100%;
          text-align: center;
          align-self: unset;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--s-spacer);
          border-radius: 22px;
        }
      }

      :host([fullscreen]) .video-slide {
        padding: 0;
        height: 100%;
        width: 100%;
        display: flex;
        justify-content: center;
      }

      .image-slide {
        border-radius: var(--xs-spacer);
        position: relative;
        display: flex;
        justify-content: center;
      }

      .image-slide umb-market-button {
        background: transparent;
      }

      .image-slide umb-market-button:hover {
        color: var(--uui-color-text);
      }

      figure {
        margin: 0;
        line-height: var(--m-spacer);
        text-align: center;
        font-weight: 400;
      }

      figcaption {
        margin-top: var(--xs-spacer);
        line-height: 1.2;
        font-size: 16px;
        color: var(--dominant-color-text, black);
        padding: var(--xs-spacer);
      }

      img {
        border-radius: 6px;
        max-height: 300px;
        max-width: 100%;
        display: block;
        margin: 0 auto;
      }

      iframe {
        display: flex;
        border: none;
        border-radius: var(--xs-spacer);
        aspect-ratio: 16 / 9;
      }

      :host([fullscreen]) img,
      :host([fullscreen]) iframe {
        width: 100%;
        height: auto;
        max-height: none;
        max-width: 100%;
        aspect-ratio: 16 / 9;
        object-fit: contain;
      }

      :host([fullscreen]) figcaption {
        height: 2.4em;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      @media (min-width: 767px) {
        :host([fullscreen]) img,
        :host([fullscreen]) iframe {
          width: auto;
          height: 65vh;
        }
      }

      :host([fullscreen]) umb-market-button {
        cursor: default;
      }

      span.outline,
      :host([fullscreen]) .image-slide span.outline {
        display: none;
      }

      @media (min-width: 767px) {
        span.outline {
          display: inline;
          opacity: 0;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          transition: opacity 0.3s ease-in-out;
          z-index: 5050;
          border-radius: 100px;
          background: var(--dominant-color, var(--uui-color-current));
          color: var(--dominant-color-text, var(--uui-color-surface));
          padding: var(--uui-size-4);
        }

        .image-slide:hover span.outline {
          opacity: 1;
        }
      }

      .video-slide > a {
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        width: 48px;
        height: 48px;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        border-radius: 100px;
        text-decoration: none;
      }
      .video-slide > a svg {
        margin-left: 3px;
      }

      .video-slide > a:hover {
        --button-background: var(--dominant-color);
        --button-hover-color: var(--dominant-color-text);
      }
    `,
  ];
}
